import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkle, PaperPlaneRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";

export default function IntakeChat({ leadId }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const scrollRef = useRef(null);
  const started = useRef(false);

  const scrollToBottom = () => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  };

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      setSending(true);
      try {
        const { data } = await api.post(`/leads/${leadId}/chat`, {});
        setMessages([{ role: "assistant", content: data.reply }]);
        setDone(data.done);
      } catch {
        setMessages([{ role: "assistant", content: "Thanks for reaching out! Blake will be in touch soon." }]);
      } finally {
        setSending(false);
      }
    })();
  }, [leadId]);

  useEffect(() => { scrollToBottom(); }, [messages, sending]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending || done) return;
    setInput("");
    await sendMessage(text);
  };

  const sendMessage = async (text, label) => {
    setMessages((m) => [...m, { role: "user", content: label || text }]);
    setSending(true);
    try {
      const { data } = await api.post(`/leads/${leadId}/chat`, { message: text });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      setDone(data.done);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I had trouble there — but Blake has your info and will reach out!" }]);
    } finally {
      setSending(false);
    }
  };

  const skip = async () => {
    if (sending || done) return;
    await sendMessage("skip", "Skip");
  };

  return (
    <div className="mt-6 border border-line bg-ink/40" data-testid="intake-chat">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-line">
        <Sparkle weight="fill" size={16} className="text-gold" />
        <span className="text-sm font-heading text-white">Quick chat with our assistant</span>
      </div>

      <div ref={scrollRef} className="max-h-72 overflow-y-auto px-4 py-4 space-y-3">
        <AnimatePresence initial={false}>
          {messages.map((m, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                data-testid={`chat-msg-${m.role}`}
                className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "bg-gold text-ink rounded-2xl rounded-br-sm"
                    : "bg-surface border border-line text-white/90 rounded-2xl rounded-bl-sm"
                }`}
              >
                {m.content}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        {sending && (
          <div className="flex justify-start">
            <div className="bg-surface border border-line rounded-2xl rounded-bl-sm px-4 py-3 flex gap-1">
              {[0, 1, 2].map((i) => (
                <span key={i} className="h-1.5 w-1.5 bg-gold/70 rounded-full animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
              ))}
            </div>
          </div>
        )}
      </div>

      {!done ? (
        <form onSubmit={send} className="flex items-center gap-2 border-t border-line p-3">
          <input
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type your answer…"
            className="flex-1 bg-transparent text-white placeholder-white/35 px-2 py-2 text-sm focus:outline-none"
          />
          <button
            type="button"
            data-testid="chat-skip"
            onClick={skip}
            disabled={sending}
            className="text-xs text-white/45 hover:text-gold px-2 py-2 transition-colors disabled:opacity-40 whitespace-nowrap"
          >
            Skip
          </button>
          <button
            type="submit"
            data-testid="chat-send"
            disabled={sending || !input.trim()}
            className="inline-flex items-center justify-center h-10 w-10 bg-gold text-ink hover:bg-gold-hover transition-colors disabled:opacity-40"
            aria-label="Send"
          >
            <PaperPlaneRight weight="fill" size={16} />
          </button>
        </form>
      ) : (
        <div data-testid="chat-done" className="border-t border-line px-4 py-3 text-sm text-gold">
          ✓ All set — Blake will reach out soon. Thank you!
        </div>
      )}
    </div>
  );
}
